import React, { useState, useEffect } from 'react';
import { Search, Bell, Check, Clock, MapPin, Battery, Zap, RefreshCw, Route, Navigation, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

interface Notification {
    id: number;
    user_id: number;
    user_username: string;
    user_avatar?: string;
    source_user_id?: number;
    source_username?: string;
    notification_type: string;
    title: string;
    message: string;
    is_read: boolean;
    created_at: string;
    user_language?: string;
}

interface NotifStats {
    total: number;
    unread: number;
    today: number;
    by_type: { type: string; count: number }[];
}

const NotificationManagement: React.FC = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [stats, setStats] = useState<NotifStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [limit, setLimit] = useState(50);

    useEffect(() => {
        fetchData();
    }, [typeFilter]);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const url = typeFilter ? `https://api.lifeshare.app/api/v1/admin/notifications?notification_type=${typeFilter}` : 'https://api.lifeshare.app/api/v1/admin/notifications';
            const [notifsRes, statsRes] = await Promise.all([
                axios.get(url),
                axios.get('https://api.lifeshare.app/api/v1/admin/notifications/stats')
            ]);
            setNotifications(notifsRes.data.notifications || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const getTypeConfig = (type: string) => {
        switch (type) {
            case 'arrival': return { icon: <MapPin className="w-4 h-4" />, label: 'Varış', bg: 'bg-emerald-100', text: 'text-emerald-600', dot: 'bg-emerald-500' };
            case 'departure': return { icon: <Navigation className="w-4 h-4" />, label: 'Ayrılış', bg: 'bg-rose-100', text: 'text-rose-600', dot: 'bg-rose-500' };
            case 'low_battery': return { icon: <Battery className="w-4 h-4" />, label: 'Düşük Pil', bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500' };
            case 'speed_alert': return { icon: <Zap className="w-4 h-4" />, label: 'Hız Uyarısı', bg: 'bg-amber-100', text: 'text-amber-600', dot: 'bg-amber-500' };
            case 'trip_completed': return { icon: <CheckCircle2 className="w-4 h-4" />, label: 'Yolculuk Tamamlandı', bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500' };
            case 'route_deviation': return { icon: <Route className="w-4 h-4" />, label: 'Rota Sapması', bg: 'bg-violet-100', text: 'text-violet-600', dot: 'bg-violet-500' };
            default: return { icon: <Bell className="w-4 h-4" />, label: type, bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500' };
        }
    };

    const getLangFlag = (lang?: string) => {
        const flags: Record<string, string> = { tr: '🇹🇷', en: '🇬🇧', de: '🇩🇪', es: '🇪🇸', fr: '🇫🇷', it: '🇮🇹', pt: '🇵🇹', nl: '🇳🇱', ru: '🇷🇺', ar: '🇸🇦', zh: '🇨🇳', ja: '🇯🇵', ko: '🇰🇷' };
        return lang ? flags[lang] || '🌐' : null;
    };

    const getTimeAgo = (dateStr: string) => {
        const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
        if (diff < 60) return 'Az önce';
        if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
        if (diff < 604800) return `${Math.floor(diff / 86400)} gün`;
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    const filteredNotifications = notifications.filter(n =>
        (n.user_username || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (n.title || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (n.message || '').toLowerCase().includes(searchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Bildirimler</h1>
                        <p className="text-sm text-gray-500 mt-1">Akıllı bildirimleri görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Bell className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Clock className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-red-700">{stats.unread}</p>
                            <p className="text-xs text-red-600">Okunmamış</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Check className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.today}</p>
                            <p className="text-xs text-green-600">Bugün</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Bell className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{(stats.by_type || []).length}</p>
                            <p className="text-xs text-blue-600">Tür Sayısı</p>
                        </div>
                    </div>
                )}

                {/* Type Filters */}
                {stats && (stats.by_type || []).length > 0 && (
                    <div className="bg-white rounded-2xl p-4">
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setTypeFilter('')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!typeFilter ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Tümü</button>
                            {stats.by_type.map((t, idx) => {
                                const config = getTypeConfig(t.type);
                                return (
                                    <button key={idx} onClick={() => setTypeFilter(t.type)} className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${typeFilter === t.type ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                        {config.icon} {config.label} ({t.count})
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı veya mesaj ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* Notification Cards */}
                {filteredNotifications.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Bildirim bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredNotifications.slice(0, limit).map((notif) => {
                            const config = getTypeConfig(notif.notification_type);
                            return (
                                <div key={notif.id} className={`bg-white rounded-2xl p-5 hover:ring-2 hover:ring-purple-100 transition-all relative overflow-hidden ${!notif.is_read ? 'ring-1 ring-purple-200' : ''}`}>
                                    {/* Unread indicator */}
                                    {!notif.is_read && (
                                        <div className="absolute top-0 left-0 w-1 h-full bg-purple-500 rounded-r" />
                                    )}

                                    {/* Top row: Type badge + Time */}
                                    <div className="flex items-center justify-between mb-3">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                                            {config.icon}
                                            {config.label}
                                        </span>
                                        <span className="text-xs text-gray-400">{getTimeAgo(notif.created_at)}</span>
                                    </div>

                                    {/* Title + Message */}
                                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{notif.title}</h3>
                                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{notif.message}</p>

                                    {/* Bottom: User info */}
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                        <div className="flex items-center gap-2.5">
                                            {notif.user_avatar ? (
                                                <img src={`https://api.lifeshare.app${notif.user_avatar}`} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-purple-600 text-xs font-semibold">{(notif.user_username || '?').charAt(0).toUpperCase()}</span>
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <p className="text-xs font-medium text-gray-700 truncate">@{notif.user_username || 'bilinmiyor'}</p>
                                                {notif.source_username && (
                                                    <p className="text-[10px] text-gray-400 truncate">← {notif.source_username}</p>
                                                )}
                                            </div>
                                            {getLangFlag(notif.user_language) && <span className="text-sm">{getLangFlag(notif.user_language)}</span>}
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${notif.is_read ? 'bg-gray-100 text-gray-500' : 'bg-purple-100 text-purple-700'}`}>
                                            {notif.is_read ? '✓ Okundu' : '● Yeni'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Load More */}
                {filteredNotifications.length > limit && (
                    <div className="text-center">
                        <button onClick={() => setLimit(l => l + 50)} className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors">
                            Daha fazla yükle ({limit} / {filteredNotifications.length})
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationManagement;
